<script setup>
import { useI18n } from '../../i18n.js'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/solid'
import Pts2EntityBadge from './Pts2EntityBadge.vue'

const { locale, t } = useI18n()

const props = defineProps({ detail: { type: Object, default: null }, loading: Boolean, error: { type: String, default: '' } })
const emit = defineEmits(['close', 'retry'])
const panel = ref(null)
let previousFocus
let previousBodyPaddingRight

function close() { emit('close') }
function keydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return }
  if (event.key !== 'Tab') return
  const items = [...(panel.value?.querySelectorAll('button,[href],[tabindex]:not([tabindex="-1"])') ?? [])]
    .filter((item) => !item.disabled)
  if (!items.length) return
  const first = items[0], last = items.at(-1)
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
function number(value, digits = 1) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : String(value)
}
function volume(value) { return value == null ? '—' : `${number(value, 0)} ${t('pts2UiHistoryLiters')}` }
function date(value, timeZone) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (!Number.isFinite(parsed.getTime())) return String(value)
  const format = { dateStyle: 'short', timeStyle: 'medium' }
  if (timeZone) format.timeZone = timeZone
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(parsed)
}
function sourceTime(instant, local, quality, timeZone) {
  if (instant) return date(instant, timeZone)
  if (!local) return '—'
  const suffix = quality === 'AMBIGUOUS' ? t('pts2UiHistoryAmbiguousLocalTime') : quality === 'INVALID' ? t('pts2UiHistoryInvalidTime') : ''
  return `${local.replace('T', ', ')}${suffix}`
}
function title(delivery) {
  if (!delivery) return t('pts2UiHistoryDelivery')
  let value = delivery.startTimeLocal
  if (delivery.startedAt) {
    const format = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    if (delivery.stationTimeZone) format.timeZone = delivery.stationTimeZone
    value = new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(new Date(delivery.startedAt))
  } else {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value ?? '')
    value = match ? `${match[3]}.${match[2]}.${match[1]}, ${match[4]}:${match[5]}` : sourceTime(null, value, delivery.startTimeQuality)
  }
  return t('pts2UiHistoryDeliveryFrom', { time: value })
}
function duration(value) {
  if (value == null) return '—'
  const hours = Math.floor(value / 3600), minutes = Math.round(value % 3600 / 60)
  return hours ? t('pts2UiHistoryHoursMinutes', { hours, minutes }) : t('pts2UiHistoryMinutes', { minutes })
}
function tank(value) { return value === 0 ? t('pts2UiHistoryAllTanks') : value == null ? '—' : t('pts2UiHistoryTankNumber', { number: value }) }
function fuel(delivery) {
  if (delivery?.fuelGradeName) return delivery.fuelGradeName
  return delivery?.fuelGradeId == null ? '—' : t('pts2UiHistoryFuelId', { id: delivery.fuelGradeId })
}

onMounted(async () => {
  previousFocus = document.activeElement
  previousBodyPaddingRight = document.body.style.paddingRight
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
  if (scrollbarWidth > 0) {
    const currentPadding = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0
    document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`
  }
  document.body.classList.add('drawer-open')
  document.addEventListener('keydown', keydown, true)
  await nextTick(); panel.value?.querySelector('.pts2-drawer-close')?.focus()
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', keydown, true)
  document.body.classList.remove('drawer-open')
  document.body.style.paddingRight = previousBodyPaddingRight || ''
  const target = previousFocus
  setTimeout(() => target?.isConnected && target.focus(), 0)
})
watch(() => props.loading, async (value, previous) => {
  if (previous && !value) {
    await nextTick()
    if (!panel.value?.contains(document.activeElement)) panel.value?.querySelector('.pts2-drawer-close')?.focus()
  }
})
</script>

<template>
  <div class="pts2-transaction-drawer-layer">
    <button class="pts2-transaction-backdrop" type="button" :aria-label="t('pts2UiHistoryCloseDetails')" @click="close"></button>
    <aside ref="panel" class="pts2-transaction-drawer pts2-delivery-drawer" role="dialog" aria-modal="true"
      aria-labelledby="pts2-delivery-title">
      <header><div><small>{{ t('pts2UiHistoryDeliveryDetails') }}</small><h2 id="pts2-delivery-title">{{ title(detail?.delivery) }}</h2></div>
        <button class="pts2-drawer-close" type="button" :aria-label="t('pts2UiHistoryCloseDetails')" @click="close"><XMarkIcon aria-hidden="true" /></button>
      </header>
      <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2UiHistoryLoadingDeliveryDetails') }}</p>
      <div v-else-if="error" class="pts2-drawer-error" role="alert"><p class="notice error">{{ error }}</p>
        <button class="pc-secondary" type="button" @click="emit('retry')">{{ t('pts2UiHistoryRetry') }}</button></div>
      <div v-else-if="detail" class="pts2-transaction-detail pts2-delivery-detail">
        <section class="pts2-transaction-summary pts2-delivery-summary">
          <span>{{ t('pts2UiHistoryPtsDeliveryVolume') }}</span><strong>{{ volume(detail.absoluteValues.productVolume) }}</strong>
          <div><p><span>{{ t('pts2UiHistoryTank') }}</span><b>{{ tank(detail.delivery.tankNumber) }}</b></p>
            <p><span>{{ t('pts2UiHistoryFuel') }}</span><Pts2EntityBadge :entity-id="detail.delivery.fuelGradeId" :label="fuel(detail.delivery)" /></p>
            <p><span>{{ t('pts2UiHistoryDuration') }}</span><b>{{ duration(detail.delivery.durationSeconds) }}</b></p></div>
        </section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryAtStart') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryTime') }}<small>DateTime</small></dt><dd>{{ sourceTime(detail.delivery.startedAt, detail.startValues.dateTimeLocal, detail.startValues.timeQuality, detail.delivery.stationTimeZone) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryProductHeight') }}<small>ProductHeight</small></dt><dd>{{ number(detail.startValues.productHeight) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryWaterHeight') }}<small>WaterHeight</small></dt><dd>{{ number(detail.startValues.waterHeight) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryTemperature') }}<small>Temperature</small></dt><dd>{{ number(detail.startValues.temperature) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryProductLiters') }}<small>ProductVolume</small></dt><dd>{{ number(detail.startValues.productVolume, 0) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryCompensatedLiters') }}<small>ProductTCVolume</small></dt><dd>{{ number(detail.startValues.productTcVolume, 0) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryDensity') }}<small>ProductDensity</small></dt><dd>{{ number(detail.startValues.productDensity) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryMass') }}<small>ProductMass</small></dt><dd>{{ number(detail.startValues.productMass, 0) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryAtEnd') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryTime') }}<small>DateTime</small></dt><dd>{{ sourceTime(detail.delivery.eventAt, detail.endValues.dateTimeLocal, detail.endValues.timeQuality, detail.delivery.stationTimeZone) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryProductHeight') }}<small>ProductHeight</small></dt><dd>{{ number(detail.endValues.productHeight) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryWaterHeight') }}<small>WaterHeight</small></dt><dd>{{ number(detail.endValues.waterHeight) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryTemperature') }}<small>Temperature</small></dt><dd>{{ number(detail.endValues.temperature) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryProductLiters') }}<small>ProductVolume</small></dt><dd>{{ number(detail.endValues.productVolume, 0) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryCompensatedLiters') }}<small>ProductTCVolume</small></dt><dd>{{ number(detail.endValues.productTcVolume, 0) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryDensity') }}<small>ProductDensity</small></dt><dd>{{ number(detail.endValues.productDensity) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryMass') }}<small>ProductMass</small></dt><dd>{{ number(detail.endValues.productMass, 0) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryPtsDelivery') }}</h3><dl>
          <div><dt>{{ t('pts2UiHistoryProductHeight') }}<small>ProductHeight</small></dt><dd>{{ number(detail.absoluteValues.productHeight) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryWaterHeight') }}<small>WaterHeight</small></dt><dd>{{ number(detail.absoluteValues.waterHeight) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryTemperature') }}<small>Temperature</small></dt><dd>{{ number(detail.absoluteValues.temperature) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryDeliveryLiters') }}<small>ProductVolume</small></dt><dd>{{ number(detail.absoluteValues.productVolume, 0) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryCompensatedDeliveryLiters') }}<small>ProductTCVolume</small></dt><dd>{{ number(detail.absoluteValues.productTcVolume, 0) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryDensity') }}<small>ProductDensity</small></dt><dd>{{ number(detail.absoluteValues.productDensity) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryMass') }}<small>ProductMass</small></dt><dd>{{ number(detail.absoluteValues.productMass, 0) }}</dd></div>
          <div class="wide pts2-delivery-pumps"><dt>{{ t('pts2UiHistoryPumpsLiters') }}<small>PumpsDispensedVolume</small></dt><dd>{{ number(detail.absoluteValues.pumpsDispensedVolume, 1) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryTimeSource') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryDeliveryEnded') }}</dt><dd>{{ sourceTime(detail.delivery.eventAt, detail.delivery.eventTimeLocal, detail.delivery.eventTimeQuality, detail.delivery.stationTimeZone) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryReceived') }}</dt><dd>{{ date(detail.delivery.receivedAt, detail.delivery.stationTimeZone) }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryStation') }}</dt><dd>{{ detail.stationName || '—' }}</dd></div><div><dt>{{ t('pts2UiHistoryController') }}</dt><dd>{{ detail.controllerName || '—' }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryPacket') }}</dt><dd>{{ detail.packetType }}</dd></div><div><dt>ConfigurationId</dt><dd>{{ detail.configurationId || '—' }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryTransport') }}</dt><dd>{{ detail.transport }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryUploadedRecords') }}</dt><dd>{{ t('pts2UiHistoryOf', { count: detail.deliveriesUploaded ?? '—', total: detail.deliveriesTotal ?? '—' }) }}</dd></div>
        </dl></section>
      </div>
      <footer><button class="pc-secondary" type="button" @click="close">{{ t('pts2UiHistoryClose') }}</button></footer>
    </aside>
  </div>
</template>
